// aidasapps/frontend/src/hooks/useChartData.js
import { useState, useEffect, useRef } from 'react';
import { interactiveFetch } from '../api';
import { queryBatcher } from './DashboardQueryBatcher';

const markWidgetQueryPending = (delta) => {
    if (typeof window === 'undefined') return;
    const current = Number(window.__widgetApiPending || 0);
    const next = Math.max(0, current + delta);
    window.__widgetApiPending = next;
    try {
        window.dispatchEvent(new CustomEvent('widget-api-pending', { detail: { pending: next } }));
    } catch {
        // non-fatal
    }
};

export const useChartData = (widget, dataset, additionalFilters = {}, customMeasures = [], dataModel = null, filtersReady = true) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Data Dependency Key
    const config = widget?.config;
    // Prefer widget.type when available; fall back to legacy config.chartType.
    // This keeps behavior compatible for old saved dashboards while avoiding stale type checks.
    const widgetType = widget?.type || config?.chartType;

    // Charts that read metrics from y_series in /dashboards/query must have at least one
    // bound measure (or heatmap legend / sankey value / scatter axis). Otherwise DuckDB builds
    // SELECT x_field AS x_val only → meta.columns = ['x_val'] and the chart renders empty.
    const chartCanRunQuery = (ct, cfg) => {
        if (!ct || ['table', 'filter', 'text', 'pageHeader'].includes(ct)) return true;
        if (ct === 'pivot') {
            // Rows-only: flat table mode — just SELECT the row fields, no aggregation needed
            if (cfg?.rows?.length > 0) return true;
            // Full matrix pivot: needs columns + values
            const hasValues = (cfg?.values?.length > 0) || (cfg?.yAxis?.length > 0) || (cfg?.series?.length > 0);
            return (cfg?.columns?.length > 0) && hasValues;
        }
        if (ct === 'heatmap') return (cfg?.legend?.length > 0);
        if (ct === 'sankey') return (cfg?.value?.length > 0);
        if (ct === 'scatter' || ct === 'bubble') {
            return (cfg?.xAxis?.length > 0) || (cfg?.yAxis?.length > 0);
        }
        if (ct === 'combo') return (cfg?.yAxis?.length > 0) || (cfg?.lineMeasures?.length > 0);
        if (ct === 'treemap') return (cfg?.yAxis?.length > 0);
        return (cfg?.yAxis?.length > 0) || (cfg?.series?.length > 0);
    };

    // Check if any data fields are configured (Expanded for all chart types)
    const hasDataConfig = (dataset?.id || dataModel?.id) && (
        (config?.xAxis?.length > 0) ||
        (config?.yAxis?.length > 0) ||
        (config?.series?.length > 0) ||
        // Pivot Table
        (config?.rows?.length > 0) ||
        (config?.columns?.length > 0) ||
        (config?.values?.length > 0) ||
        // Sankey
        (config?.source?.length > 0) ||
        (config?.target?.length > 0) ||
        // Combo
        (config?.lineMeasures?.length > 0) ||
        // Treemap optional color metric only (size still required for meaningful chart)
        ((widgetType === 'treemap' || config?.chartType === 'treemap') && (config?.color?.length > 0)) ||
        // Other (Gauge, KPI, etc might use target/min/max but usually have yAxis too)
        (config?.element?.length > 0) || // Generic fallback
        // Legacy / Table specific
        (widgetType === 'table' && config?.query_config?.selected_columns?.length > 0)
    ) && chartCanRunQuery(widgetType, config);

    // Data Dependency Key
    const dataKey = JSON.stringify({
        datasetId: dataset?.id,
        dataModelId: dataModel?.id,  // trigger refetch on model change
        widgetType: widgetType,  // Track widget type changes from config
        widgetTypeActual: widget?.type,  // Track actual widget.type (authoritative for combo etc.)
        xAxis: config?.xAxis,
        yAxis: config?.yAxis,
        series: config?.series,
        legend: config?.legend,  // Track heatmap legend (y-axis values)
        columns: config?.columns,  // Track table columns
        // New Fields for Pivot / Sankey / Combo
        rows: config?.rows,
        values: config?.values,
        value: config?.value,
        source: config?.source,
        target: config?.target,
        min: config?.min,
        max: config?.max,
        comparison: config?.comparison,
        lineMeasures: config?.lineMeasures,
        color: config?.color,

        filters: config?.filters,
        additionalFilters: additionalFilters, // Trigger refetch on global filter change
        extraFields: config?.style?.tooltip?.extraFields,
        query_config: config?.query_config,
        data_limit: config?.data?.limit,
        data_sort: config?.data?.sort_by,
        sort_dir_legacy: config?.query_config?.sortDir || config?.query_config?.sortOrder,
        custom_measures: customMeasures, // Trigger refetch on formula update
        filtersReady, // Block fetch until filters are initialized
    });

    const prevKeyRef = useRef(null);

    useEffect(() => {
        const prevKey = prevKeyRef.current;
        prevKeyRef.current = dataKey; // Update for next time

        // Clear stale error immediately so "Data Unavailable" doesn't persist
        // while re-querying after a field is added/changed.
        setError(null);

        // CRITICAL: Don't query if neither dataset nor data model is loaded yet
        if (!dataset?.id && !dataModel?.id) {
            setData([]);
            return;
        }

        if (!hasDataConfig) {
            // DIAGNOSTIC: Log why the query is being blocked
            console.warn('[useChartData] BLOCKED — hasDataConfig=false', {
                widgetType,
                widgetTypeRaw: widget?.type,
                configChartType: config?.chartType,
                dataModelId: dataModel?.id,
                datasetId: dataset?.id,
                rows: config?.rows,
                columns: config?.columns,
                values: config?.values,
                yAxis: config?.yAxis,
                series: config?.series,
                chartCanRunResult: chartCanRunQuery(widgetType, config),
                fullConfig: config,
            });
            setData([]);
            return;
        }

        // FILTER GATE: Don't fetch until auto-selected filters are resolved
        if (!filtersReady) {
            return;
        }

        // Abort Controller for Request Cancellation
        const controller = new AbortController();

        const fetchData = async () => {
            let pendingMarked = false;
            setLoading(true);
            setError(null);
            markWidgetQueryPending(1);
            pendingMarked = true;

            try {
                // 1. Construct WidgetQueryRequest Payload

                const normalizeAgg = (agg) => {
                    if (!agg) return 'sum';
                    const lower = agg.toLowerCase();
                    if (lower === 'average') return 'avg';
                    if (lower === 'unique') return 'count_distinct';
                    if (lower === 'sum' || lower === 'avg' || lower === 'min' || lower === 'max' || lower === 'count' || lower === 'distinct' || lower === 'count_distinct') {
                        return lower;
                    }
                    return 'sum';
                };

                // Identify Chart Type: widget.type is always authoritative.
                // config.chartType can be stale (e.g. 'bar') after user switches chart type to 'combo',
                // because GalleryView only updates widget.type, not config.chartType.
                // Using stale config.chartType would cause the combo branch to be skipped,
                // leaving lineMeasures out of y_series entirely.
                const chartType = widget.type || config.chartType;

                let xField = null;
                let groupField = config.query_config?.groupField || null;
                let sourceSeries = config.yAxis || config.series || [];

                // --- TYPE SPECIFIC MAPPING ---
                if (chartType === 'heatmap') {
                    // Heatmap: X=Dimension, Y=GroupDimension, Value=Measure
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        xField = typeof config.xAxis[0] === 'string' ? config.xAxis[0] : config.xAxis[0].name;
                    }
                    if (Array.isArray(config.yAxis) && config.yAxis.length > 0) {
                        // Map Y-Axis config to Group Field
                        groupField = typeof config.yAxis[0] === 'string' ? config.yAxis[0] : config.yAxis[0].name;
                    }
                    // Value comes from Legend zone in our Toolbox mapping
                    if (config.legend && config.legend.length > 0) {
                        sourceSeries = config.legend;
                    } else {
                        sourceSeries = []; // No value mapped
                    }

                } else if (['scatter', 'bubble'].includes(chartType)) {
                    // Scatter: X=Measure, Y=Measure, Legend=Dimension (Dot)
                    if (Array.isArray(config.legend) && config.legend.length > 0) {
                        xField = typeof config.legend[0] === 'string' ? config.legend[0] : config.legend[0].name;
                    }
                    // Collect measures from X and Y axis zones
                    sourceSeries = [...(config.xAxis || []), ...(config.yAxis || [])];

                } else if (['kpi', 'gauge'].includes(chartType)) {
                    // KPI/Gauge: Map yAxis, target, comparison with roles
                    const main = (config.yAxis || []).map(s => ({ ...s, role: 'yAxis' }));
                    const targets = (config.target || []).map(s => ({ ...s, role: 'target' }));
                    const comparisons = (config.comparison || []).map(s => ({ ...s, role: 'comparison' }));

                    sourceSeries = [...main, ...targets, ...comparisons];

                } else if (chartType === 'sankey') {
                    // Sankey: source -> xField, target -> groupField, value -> sourceSeries
                    if (Array.isArray(config.source) && config.source.length > 0) {
                        xField = typeof config.source[0] === 'string' ? config.source[0] : (config.source[0].name || config.source[0].field);
                    }
                    if (Array.isArray(config.target) && config.target.length > 0) {
                        groupField = typeof config.target[0] === 'string' ? config.target[0] : (config.target[0].name || config.target[0].field);
                    }
                    sourceSeries = [
                        ...(config.source || []).map(s => ({ ...s, role: 'source' })),
                        ...(config.target || []).map(s => ({ ...s, role: 'target' })),
                        ...(config.value || []).map(s => ({ ...s, role: 'value' }))
                    ];

                } else if (chartType === 'pivot') {
                    // Pivot: rows -> xField, columns -> groupField, values -> series
                    if (Array.isArray(config.rows) && config.rows.length > 0) {
                        xField = typeof config.rows[0] === 'string' ? config.rows[0] : (config.rows[0].name || config.rows[0].field);
                    }
                    if (Array.isArray(config.columns) && config.columns.length > 0) {
                        groupField = typeof config.columns[0] === 'string' ? config.columns[0] : (config.columns[0].name || config.columns[0].field);
                    }
                    sourceSeries = config.values || config.yAxis || config.series || [];

                } else if (chartType === 'combo') {
                    // Combo: xAxis -> xField, yAxis (Bars) + lineMeasures (Lines) -> series
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        xField = typeof config.xAxis[0] === 'string' ? config.xAxis[0] : (config.xAxis[0].name || config.xAxis[0].field);
                    }
                    // Combine Bars (yAxis) and Lines (lineMeasures)
                    const bars = config.yAxis || [];
                    const lines = (config.lineMeasures || []).map(s => ({ ...s, type: 'line' })); // Tag as line if needed
                    sourceSeries = [...bars, ...lines];

                } else if (chartType === 'treemap') {
                    // Size = yAxis, optional color = color drop zone (not a second yAxis measure)
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        const xObj = config.xAxis[0];
                        xField = typeof xObj === 'string' ? xObj : (xObj?.name || xObj?.field);
                    }
                    sourceSeries = [
                        ...(config.yAxis || []).map(s =>
                            typeof s === 'object' && s !== null ? { ...s, role: 'yAxis' } : { name: s, field: s, role: 'yAxis' }
                        ),
                        ...(config.color || []).map(s =>
                            typeof s === 'object' && s !== null ? { ...s, role: 'treemapColor' } : { name: s, field: s, role: 'treemapColor' }
                        )
                    ];

                } else {
                    // Default Cartesian (Bar, Line, Area, Pie)
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        const xObj = config.xAxis[0];
                        xField = typeof xObj === 'string' ? xObj : (xObj?.name || xObj?.field);
                    } else {
                        // console.log("[useChartDataNew] xAxis invalid or empty");
                    }

                    // Stack/Group: legend zone holds the split-by dimension for bar/line/area
                    if (Array.isArray(config.legend) && config.legend.length > 0) {
                        const legendObj = config.legend[0];
                        groupField = typeof legendObj === 'string' ? legendObj : (legendObj?.name || legendObj?.field);
                    } else if (Array.isArray(config.series) && config.series.length > 0) {
                        const legendObj = config.series[0];
                        groupField = typeof legendObj === 'string' ? legendObj : (legendObj?.name || legendObj?.field);
                    }
                }

                const ySeries = sourceSeries.map((s, idx) => {
                    // Prefer physical column name (field) over display name — same as parquet / SQL.
                    const fieldName = typeof s === 'string' ? s : (s.field || s.name);
                    // BOXPLOT: always force 'none' aggregation to get raw values for distribution
                    const agg = chartType === 'boxplot' ? 'none' : normalizeAgg(s.agg);
                    return {
                        id: s.id || `s_${idx}`,
                        field: fieldName,
                        name: s.name || fieldName,
                        agg: agg,
                        label: s.label || fieldName,
                        color: s.color || null,
                        role: s.role // Pass role (confidence, target, etc)
                    };
                }).filter(s => s.field);

                const exemptEmptyYSeries = ['table', 'pivot', 'filter', 'text', 'pageHeader'];
                if (!exemptEmptyYSeries.includes(chartType) && ySeries.length === 0) {
                    console.warn(
                        '[useChartDataNew] Skipping dashboard query: no y_series measures.',
                        { chartType, hint: chartType === 'heatmap' ? 'Add a field to Legend (heat value).' : 'Add a measure to Y axis (or required drop zone).' }
                    );
                    if (!controller.signal.aborted) {
                        setData([]);
                    }
                    return;
                }

                let columns = null;
                let pivotRows = null;
                let pivotCols = null;
                if (chartType === 'table') {
                    const rawCols = config.columns || config.query_config?.selected_columns;
                    // columns may be strings OR objects with name|field|id
                    columns = rawCols?.map(c =>
                        typeof c === 'string' ? c : (c?.name || c?.field || c?.id)
                    ).filter(Boolean) || [];
                } else if (chartType === 'pivot') {
                    // Send ALL rows, columns, and values for proper DuckDB pivot
                    pivotRows = (config.rows || []).map(r =>
                        typeof r === 'string' ? r : (r?.name || r?.field)
                    ).filter(Boolean);
                    pivotCols = (config.columns || []).map(c =>
                        typeof c === 'string' ? c : (c?.name || c?.field)
                    ).filter(Boolean);
                }

                // Pivot value fields with agg (sent separately so backend has field+agg without needing to
                // cross-reference y_series — avoids id mismatches)
                const VALID_AGGS = new Set(['sum', 'avg', 'min', 'max', 'count', 'count_distinct', 'first', 'last']);
                const pivotValues = chartType === 'pivot'
                    ? (config.values || config.yAxis || config.series || []).map(v => {
                        const field = typeof v === 'string' ? v : (v?.name || v?.field);
                        const rawAgg = (v?.agg || '').toLowerCase().trim();
                        // Sanitize: '-' or 'none' or '' all default to 'sum'
                        const agg = VALID_AGGS.has(rawAgg) ? rawAgg : 'sum';
                        return { field, agg, id: v?.id || field };
                    }).filter(pv => pv.field)
                    : null;

                const limit = config.query_config?.limit || config.data?.limit || 1000;
                // Relaxed Priority: attributes in 'data' take precedence, but fallback to 'query_config'
                // if not present. This ensures that if the UI only updates 'query_config' (legacy),
                // we still respect it even if 'data' object exists.
                let sortBy = config.data?.sort_by ?? config.query_config?.sortBy;

                let rawSortDir = config.query_config?.sortDir || config.query_config?.sortOrder || config.data?.sort_dir || 'desc';
                if (!rawSortDir) rawSortDir = 'desc';

                let sortDir = 'desc';
                const lowerSortDir = rawSortDir.toString().toLowerCase();

                if (lowerSortDir.includes('asc')) {
                    sortDir = 'asc';
                } else if (lowerSortDir.includes('desc')) {
                    sortDir = 'desc';
                }

                if (sortBy === 'Default' || !sortBy) {
                    sortBy = null;
                }

                if (sortBy) {
                    const matchedSeries = ySeries.find(s => s.field === sortBy || s.label === sortBy);
                    if (matchedSeries) {
                        sortBy = matchedSeries.id;
                    }
                }

                // Merge Widget Filters + Global Filters
                const globalFilterList = Object.entries(additionalFilters).map(([k, v]) => ({
                    field: k,
                    operator: Array.isArray(v) ? 'in' : 'equals',
                    value: v
                }));
                const mergedFilters = [...(config.filters || []), ...globalFilterList];

                const pathDashId = window.location.pathname.split('/').pop();
                const tempDashId = (pathDashId && pathDashId !== 'new' && pathDashId !== 'dashboard') ? pathDashId : "temp-dashboard-batch";
                const activeDashboardId = widget.dashboardId || widget.dashboard_id || tempDashId;

                const payload = {
                    dashboard_id: activeDashboardId,
                    dataset_id: dataModel?.id ? undefined : (dataset?.id || undefined),
                    data_model_id: dataModel?.id || undefined,
                    limit: parseInt(limit),
                    x_field: xField,
                    y_series: ySeries,
                    columns: columns,
                    filters: mergedFilters,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                    group_field: groupField,
                    rank_by: null,
                    custom_measures: customMeasures,
                    source_date_format: config.query_config?.sourceDateFormat,
                    // Pivot-specific: full row/col/value field lists
                    ...(pivotRows ? { pivot_rows: pivotRows } : {}),
                    ...(pivotCols ? { pivot_cols: pivotCols } : {}),
                    ...(pivotValues ? { pivot_values: pivotValues } : {}),
                };

                // Pass Signal to API
                // Dashboard queries go to the interactive runner (port 8001)
                // so they run independently from the main API (port 8000)
                
                const isKpiWithComparison = ['kpi', 'gauge', 'ragKpi'].includes(chartType) && ((config.comparison || []).length > 0);
                
                let result = null;
                
                if (isKpiWithComparison && mergedFilters.length > 0) {
                    let hasShiftableFilter = false;
                    
                    const shiftDateBackwardSingle = (val) => {
                        if (typeof val !== 'string') return val;
                        const mmmMatch = val.match(/^([a-zA-Z]+)\s*(\d{4})$/);
                        if (mmmMatch) {
                           const monthStr = mmmMatch[1];
                           const year = parseInt(mmmMatch[2], 10);
                           const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
                           const fullMonths = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
                           
                           let lowerMonth = monthStr.toLowerCase();
                           let mIdx = fullMonths.indexOf(lowerMonth);
                           let isFull = true;
                           if (mIdx === -1) {
                               mIdx = months.indexOf(lowerMonth.substring(0, 3));
                               isFull = false;
                           }
                           
                           if (mIdx !== -1) {
                               mIdx -= 1;
                               let newYear = year;
                               if (mIdx < 0) {
                                   mIdx = 11;
                                   newYear -= 1;
                               }
                               let newMonthStr = isFull ? fullMonths[mIdx] : months[mIdx];
                               if (monthStr[0] === monthStr[0].toUpperCase()) newMonthStr = newMonthStr.charAt(0).toUpperCase() + newMonthStr.slice(1);
                               if (monthStr === monthStr.toUpperCase()) newMonthStr = newMonthStr.toUpperCase();
                               
                               const space = val.includes(' ') ? ' ' : '';
                               hasShiftableFilter = true;
                               return `${newMonthStr}${space}${newYear}`;
                           }
                        }
                        
                        const isoMatch = val.match(/^(\d{4})-(\d{2})$/);
                        if (isoMatch) {
                            let y = parseInt(isoMatch[1], 10);
                            let m = parseInt(isoMatch[2], 10);
                            m -= 1;
                            if (m < 1) { m = 12; y -= 1; }
                            hasShiftableFilter = true;
                            return `${y}-${m.toString().padStart(2, '0')}`;
                        }
                        
                        const slashMatch = val.match(/^(\d{1,2})\/(\d{4})$/);
                        if (slashMatch) {
                            let m = parseInt(slashMatch[1], 10);
                            let y = parseInt(slashMatch[2], 10);
                            m -= 1;
                            if (m < 1) { m = 12; y -= 1; }
                            hasShiftableFilter = true;
                            return `${m.toString().padStart(slashMatch[1].length, '0')}/${y}`;
                        }
                        return val;
                    };

                    const shiftDateBackward = (val) => {
                        if (Array.isArray(val)) {
                            return val.map(shiftDateBackwardSingle);
                        }
                        return shiftDateBackwardSingle(val);
                    };
                    
                    const shiftedFilters = mergedFilters.map(f => {
                         if (!f.value) return f;
                         const shiftedVal = shiftDateBackward(f.value);
                         return { ...f, value: shiftedVal };
                    });
                    
                    if (hasShiftableFilter) {
                         const [mainResult, prevResult] = await Promise.all([
                             queryBatcher.fetch(payload.dashboard_id, widget.id, payload, controller.signal).catch(() => null),
                             queryBatcher.fetch(payload.dashboard_id, widget.id + '_prev', { ...payload, filters: shiftedFilters }, controller.signal).catch(() => null)
                         ]);
                         
                         result = mainResult;
                         let mainData = Array.isArray(mainResult?.data) ? mainResult.data : (Array.isArray(mainResult) ? mainResult : []);
                         let prevData = Array.isArray(prevResult?.data) ? prevResult.data : (Array.isArray(prevResult) ? prevResult : []);
                         
                         if (mainData.length > 0 && prevData.length > 0) {
                             const compIds = (config.comparison || []).map(c => c.id || c.name || c.field);
                             compIds.forEach(id => {
                                 if (prevData[0].hasOwnProperty(id)) {
                                     mainData[0][id + '_prev'] = prevData[0][id]; // Merge uniquely for comparison
                                 }
                             });
                         }
                    } else {
                        result = await queryBatcher.fetch(payload.dashboard_id, widget.id, payload, controller.signal);
                    }
                } else {
                    result = await queryBatcher.fetch(payload.dashboard_id, widget.id, payload, controller.signal);
                }

                let rawData = [];
                if (result && Array.isArray(result.data)) {
                    rawData = result.data;
                } else if (Array.isArray(result)) {
                    rawData = result;
                } else {
                    console.warn("Unexpected data format", result);
                    rawData = [];
                }

                if (rawData.length > 0) {
                    // ── PIVOT / Grouped data normalization ──
                    // When DuckDB PIVOT returns wide-format data, the dimension column
                    // keeps its original name (e.g. "Department") instead of "x_val".
                    // Rename it so the chart builder can find it, and remove the original
                    // key so it isn't treated as a data series.
                    const firstRow = rawData[0];
                    const hasPivotDimension = xField && !firstRow.hasOwnProperty('x_val') && groupField;
                    // Find the dimension column — could be xField exactly, or a suffix match
                    let pivotDimKey = null;
                    if (hasPivotDimension) {
                        if (firstRow.hasOwnProperty(xField)) {
                            pivotDimKey = xField;
                        } else {
                            // Suffix match: xField = "Table.Column", data has "Column"
                            const xBasename = xField.split('.').pop()?.toLowerCase();
                            for (const k of Object.keys(firstRow)) {
                                if (k.split('.').pop()?.toLowerCase() === xBasename) {
                                    pivotDimKey = k;
                                    break;
                                }
                            }
                        }
                    }

                    rawData = rawData.map(d => {
                        const newRow = { ...d };

                        // Map pivoted dimension → x_val
                        if (pivotDimKey) {
                            newRow.x_val = d[pivotDimKey];
                            delete newRow[pivotDimKey]; // Don't treat dimension as a series
                        }

                        if (xField && d.hasOwnProperty('x_val')) {
                            newRow[xField] = d.x_val;
                        }

                        ySeries.forEach(s => {
                            if (d.hasOwnProperty(s.id)) {
                                newRow[s.field] = d[s.id];
                                newRow[s.id] = d[s.id]; // Expose ID directly to avoid field collisions
                            }
                        });

                        return newRow;
                    });
                }

                // ── CLIENT-SIDE PIVOT for tidy/long format grouped data ──
                // When DuckDB native PIVOT fails, data comes back in tidy format:
                //   [{x_val: "Sales", "Job Type": "Emails", s_0: 100}, ...]
                // The chart builder expects wide format:
                //   [{x_val: "Sales", "Emails": 100, "Meetings": 50}, ...]
                // Detect tidy format and pivot it.
                if (groupField && rawData.length > 0 && rawData[0].hasOwnProperty(groupField)) {
                    const measureId = ySeries[0]?.id;
                    if (measureId) {
                        const pivotMap = new Map(); // x_val → {x_val, groupVal1: val, groupVal2: val, ...}
                        rawData.forEach(row => {
                            const xKey = String(row.x_val ?? '');
                            const groupVal = String(row[groupField] ?? '');
                            const numVal = Number(row[measureId]) || 0;

                            if (!pivotMap.has(xKey)) {
                                pivotMap.set(xKey, { x_val: row.x_val });
                            }
                            pivotMap.get(xKey)[groupVal] = numVal;
                        });
                        rawData = Array.from(pivotMap.values());
                    }
                }

                // Only set data if not aborted (though catch handles it, double check)
                if (!controller.signal.aborted) {
                    setData(rawData);
                }

            } catch (err) {
                // Ignore Abort Errors
                if (err.name === 'AbortError') {
                    // console.log('[useChartDataNew] Request aborted');
                    return;
                }

                console.error("Failed to fetch chart data:", err);
                const errorMsg = err?.message || err?.error || err?.toString() || 'An error occurred while fetching data.';
                if (!controller.signal.aborted) {
                    setError({ message: errorMsg });
                    setData([]);
                }
            } finally {
                if (pendingMarked) {
                    markWidgetQueryPending(-1);
                }
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        // Cleanup: Abort request on unmount or re-execute
        return () => {
            controller.abort();
        };

    }, [dataKey]);

    return { data, loading, error };
};